import { loadSingleImage } from "./load-single-image";
import { isIdleLoadingDesired } from "./idle-loading-desired";
import { createAsyncStackTaggingLink } from "../../../user-agent-fallbacks/async-stack-tagging";

const imageLoadRequests = [] as {
  readonly imageElement: HTMLImageElement;
  readonly abortSignal: AbortSignal;
  readonly linkedTask: ReturnType<typeof createAsyncStackTaggingLink>;
}[];

export const scheduleOptionalIdleImageLoad = (
  imageElement: HTMLImageElement,
  abortSignal: AbortSignal
) => {
  imageLoadRequests.push({
    imageElement,
    abortSignal,
    linkedTask: createAsyncStackTaggingLink(
      "schedule optional idle image load"
    ),
  });
  maybeScheduleProcessingOfNextLoadRequest();
};

const processSingleImageLoadRequest = () => {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const loadRequest = imageLoadRequests.shift();

    if (loadRequest === undefined) {
      doNotScheduleProcessingOfNextLoadRequest();
      return;
    }

    // Short-circuit when the image won't be loaded.
    // The isIdleLoadingDesired check is also here, since the properties of the
    // user agent's connection can change during a navigation.
    if (
      loadRequest.abortSignal.aborted ||
      loadRequest.imageElement.complete ||
      !isIdleLoadingDesired()
    ) {
      continue;
    }

    loadRequest
      .linkedTask(
        loadSingleImage,
        loadRequest.imageElement,
        loadRequest.abortSignal
      )
      .then(forcefullyScheduleProcessingOfNextLoadRequest)
      .catch(forcefullyScheduleProcessingOfNextLoadRequest);
    return;
  }
};

let currentIdleCallbackHandle: number | undefined;

const maybeScheduleProcessingOfNextLoadRequest = () => {
  if (currentIdleCallbackHandle !== undefined) {
    return;
  }

  forcefullyScheduleProcessingOfNextLoadRequest();
};

const forcefullyScheduleProcessingOfNextLoadRequest = () => {
  currentIdleCallbackHandle = requestIdleCallback(
    processSingleImageLoadRequest
  );
};

const doNotScheduleProcessingOfNextLoadRequest = () => {
  currentIdleCallbackHandle = undefined;
};
